// Import CommonModule
import {ChangeDetectionStrategy, Component, computed, inject, input, isDevMode, model, OnInit, output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {UserModel} from '@core/api/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from '@shared/icon';
import {AccordionComponent, AccordionPanelComponent, AutoTabIndexTrapDirective} from '@shared/ui';
import {UserFormModel} from '../../models/user-form.model';
import {UserFormSection, UserFormSections} from '../../models/user-form.types';
import {UserFormFactory} from '../../services/user-form.factory';
import {UserFormChangesService} from '../../services/user-form-changes.service';
import {UserFormStatus} from '../../typings';
import {SectionValidityIndicatorComponent} from './components/section-validity-indicator.component';
import {FormChangesIndicatorComponent} from './components/form-changes-indicator.component';
import {UserFormContactInformationComponent} from './form-sections/user-form-contact-information.component';
import {UserFormGeneralDetailsComponent} from './form-sections/user-form-general-details.component';
import {UserFormPasswordComponent} from './form-sections/user-form-password.component';
import {UserFormPermissionsComponent} from './form-sections/user-form-permissions.component';
import {ActiveUserPreferencesComponent} from './form-sections/active-user-preferences.component';
import {ActiveUserStampsComponent} from './form-sections/active-user-stamps/active-user-stamps.component';
import {JsonPipe} from '@angular/common';

@Component({
	selector: 'dc-user-form',
	imports: [
		ReactiveFormsModule,
		UserFormGeneralDetailsComponent,
		UserFormPasswordComponent,
		UserFormContactInformationComponent,
		UserFormPermissionsComponent,
		SectionValidityIndicatorComponent,
		FormChangesIndicatorComponent,
		AccordionComponent,
		AccordionPanelComponent,
		TranslateModule,
		IconComponent,
		AutoTabIndexTrapDirective,
		ActiveUserPreferencesComponent,
		ActiveUserStampsComponent,
		JsonPipe,
	],
	styleUrl: './user-form.styles.scss',
	templateUrl: './user-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [UserFormFactory],
})
export class UserFormComponent implements OnInit {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	public readonly userFormFactory = inject(UserFormFactory);
	private readonly changesService = inject(UserFormChangesService);

	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	public readonly user = model<UserModel | null>(null);
	public readonly sections = input<UserFormSection[]>(UserFormSections);
	public readonly formTitle = input<string>('dccomp.user-form.create');
	public readonly formSubmitButtonText = input<string>('struct.user-edit.buttons.create');
	public readonly formCancelButtonText = input<string>('struct.user-edit.buttons.cancel');
	public readonly formStatus = model<UserFormStatus>('loading');
	public readonly showChangesIndicator = input<boolean>(true);

	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	public readonly formSubmit = output<UserFormModel>();
	public readonly formCancel = output<void>();

	/* ------------------------------------------------  Signals ------------------------------------------------ */
	public readonly isEditMode = computed(() => !!this.user());
	public readonly hasChanges = computed(() => this.changesService.hasChanges());

	/* -------------------------------------------------- Data -------------------------------------------------- */

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {
		// Build the permissions group dynamically
	}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */
	ngOnInit(): void {
		const sections = this.sections();
		this.userFormFactory.createUserForm(sections, new UserFormModel(this.user() || new UserModel()), this.formStatus, this.isEditMode());

		// Initialize change tracking if in edit mode
		if (this.isEditMode()) {
			this.initializeChangeTracking();
		}
	}

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	private initializeChangeTracking(): void {
		// Initialize change tracking service with the original user model and form group
		this.changesService.initializeChangeTracking(this.user(), this.userFormFactory.userForm, this.sections());
	}

	onSubmit(): void {
		const userForm = this.userFormFactory.userForm;
		this.formStatus.set('submitting');
		userForm.markAllAsTouched(); // Mark all as touched

		if (userForm.invalid) {
			console.log('Form Invalid:', userForm.errors);

			// Log individual control errors
			Object.keys(userForm.controls).forEach(key => {
				const controlErrors = userForm.get(key)?.errors;
				if (controlErrors != null) {
					console.log('Key control: ' + key + ', errors: ', controlErrors);
				}
			});
			if (userForm.get('permissions')?.errors) {
				console.log('Permissions errors: ', userForm.get('permissions')?.errors);
			}
			this.formStatus.set('error');
			return;
		}

		this.formStatus.set('submitting');
		const updatedForm = this.userFormFactory.getUpdateUserForm();
		this.formSubmit.emit(updatedForm);
	}

	protected readonly isDevMode = isDevMode;
}
